import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowLeft } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/geet' },
    { name: 'About', path: '/geet/about' },
    { name: 'Menu', path: '/geet/menu' },
    { name: 'Gallery', path: '/geet/gallery' },
    { name: 'Contact', path: '/geet/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled
          ? 'bg-[#1C1917]/95 backdrop-blur-md shadow-2xl'
          : 'bg-gradient-to-b from-[#1C1917]/80 to-transparent'
      }`}
    >
      {/* Top Gold Line */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C9A227] to-transparent" />
      
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20 md:h-28">
          {/* Logo + back-to-landing */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="back-to-landing"
              className="hidden md:flex items-center gap-1.5 text-[#C9A227]/70 hover:text-[#C9A227] transition-colors duration-300 font-['Cormorant_Garamond'] italic text-sm tracking-wider group"
              aria-label="Back to landing"
            >
              <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
              <span className="hidden lg:inline">All Venues</span>
            </Link>
            <Link to="/geet" className="flex items-center" data-testid="logo-link">
              <img 
                src="/geet-neon.png" 
                alt="Geet Fine Dining"
                className="h-14 md:h-16 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-12">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`font-['Cormorant_Garamond'] uppercase tracking-[0.25em] text-sm transition-all duration-300 relative group ${
                  isActive(link.path)
                    ? 'text-[#C9A227]'
                    : 'text-[#FAF6F0]/80 hover:text-[#C9A227]'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-2 left-0 h-[1px] bg-[#C9A227] transition-all duration-300 ${
                  isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/geet/contact"
            data-testid="nav-contact-cta"
            className="hidden md:block border border-[#C9A227] text-[#C9A227] px-8 py-3 hover:bg-[#C9A227] hover:text-[#1C1917] transition-all duration-500 font-['Cormorant_Garamond'] tracking-[0.2em] uppercase text-sm"
          >
            Reserve
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-[#C9A227]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-[#1C1917] border-t border-[#C9A227]/30 transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="px-6 py-10 space-y-6">
          <Link
            to="/"
            data-testid="mobile-back-to-landing"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-2 text-[#C9A227]/70 hover:text-[#C9A227] transition-colors font-['Cormorant_Garamond'] italic text-base tracking-wider pb-4 border-b border-[#C9A227]/20"
          >
            <ArrowLeft size={16} />
            All Venues
          </Link>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-${link.name.toLowerCase()}`}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block font-['Cormorant_Garamond'] uppercase tracking-[0.25em] text-lg transition-colors duration-300 ${
                isActive(link.path)
                  ? 'text-[#C9A227]'
                  : 'text-[#FAF6F0]/80 hover:text-[#C9A227]'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-4 border-t border-[#C9A227]/20">
            <Link
              to="/geet/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-center border border-[#C9A227] text-[#C9A227] px-6 py-4 hover:bg-[#C9A227] hover:text-[#1C1917] transition-all duration-300 font-['Cormorant_Garamond'] tracking-[0.2em] uppercase"
            >
              Reserve a Table
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
